import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useUserQuery } from 'api'
import Link from 'next/link'
import { media } from 'styled-bootstrap-grid'
import StaticImage from '@components/images/StaticImg'
import { ASSET_PATH, ROUTES } from '../content'
import { hairline, ink, muted } from '../styled'

// Mobile is a single row — logo, primary CTA, menu button — with the section
// links tucked into a dropdown panel. From `lg` up the links sit inline and the
// menu button disappears.
const Bar = styled.nav`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 1120px;
  margin: 0 auto;
  padding: 14px 16px;
  border-bottom: 1px solid ${hairline};

  ${media.sm`
    gap: 20px;
    padding: 20px 24px;
  `}
`

const Brand = styled.a`
  display: flex;
  align-items: center;
  margin-right: auto;

  img {
    height: 28px;
    width: auto;

    ${media.sm`height: 36px;`}
  }
`

// Five links plus a logo and a CTA do not fit a tablet, so the inline row waits for lg.
const Links = styled.div`
  display: none;

  ${media.lg`
    display: flex;
    align-items: center;
    gap: 20px;
  `}
`

// `PageRoot a` outranks a single class, hence the doubled `&&`.
const NavLink = styled.a`
  && {
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    color: ${muted};
    /* the Log Out item renders as a button; keep it visually identical */
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 0;
  }

  &&:hover {
    color: ${ink};
  }
`

const Cta = styled.button`
  && {
    background: ${ink};
    color: #ffffff;
    padding: 9px 14px;
    border-radius: 999px;
    font-weight: 600;
    font-size: 13px;
    font-family: inherit;
    white-space: nowrap;
    border: none;
    cursor: pointer;

    ${media.sm`
      padding: 10px 18px;
      font-size: 15px;
    `}
  }
`

const CtaLink = Cta.withComponent('a')

// Three bars that rotate into a cross when open.
const MenuButton = styled.button`
  display: flex;
  position: relative;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  margin-right: -10px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  ${media.lg`display: none;`}

  span {
    position: absolute;
    top: 21px;
    left: 12px;
    width: 20px;
    height: 2px;
    border-radius: 2px;
    background: ${ink};
    transition: transform 180ms ease, opacity 120ms ease;
  }

  span:nth-child(1) {
    transform: ${(props) => (props.$open ? 'rotate(45deg)' : 'translateY(-6px)')};
  }

  span:nth-child(2) {
    opacity: ${(props) => (props.$open ? 0 : 1)};
  }

  span:nth-child(3) {
    transform: ${(props) => (props.$open ? 'rotate(-45deg)' : 'translateY(6px)')};
  }
`

const Panel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 60;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-bottom: 1px solid ${hairline};
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  padding: 4px 16px 10px;

  ${media.lg`display: none;`}
`

const PanelLink = styled.a`
  && {
    display: block;
    padding: 14px 2px;
    font-size: 15px;
    font-weight: 600;
    color: ${ink};
    border-top: 1px solid #f0f0f0;
    /* the Log Out item renders as a button; keep it visually identical */
    width: 100%;
    text-align: left;
    background: none;
    border-left: none;
    border-right: none;
    border-bottom: none;
    cursor: pointer;
    font-family: inherit;
  }

  &&:first-child {
    border-top: none;
  }

  &&:hover {
    color: ${ink};
  }
`

/**
 * @param links       [{ href, label }] shown inline on desktop
 * @param menuLinks   [{ href, label }] shown in the mobile dropdown; defaults to `links`
 * @param cta         { label, href } renders an anchor, or
 * @param onCtaClick  renders a button instead (used to open the intake modal)
 */
const ProgramNav = ({ links = [], menuLinks, cta, onCtaClick }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const {
    data: { user },
  } = useUserQuery()

  // A signed-in visitor sees Account (their way into the customer portal)
  // where Log In would be, matching the site header's wording
  const withAuthState = useCallback(
    (items) =>
      items.map((item) => (user && item.href === ROUTES.signIn ? { href: '/course', label: 'Account' } : item)),
    [user],
  )

  const resolvedLinks = withAuthState(links)
  const panelLinks = withAuthState(menuLinks || links)

  useEffect(() => {
    if (!menuOpen) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [menuOpen, closeMenu])

  return (
    <Bar>
      <Link href="/" passHref>
        <Brand onClick={closeMenu}>
          <StaticImage src={`${ASSET_PATH}/es-logo.png`} alt="Entrepreneur Seminar" />
        </Brand>
      </Link>

      {resolvedLinks.length > 0 && (
        <Links>
          {resolvedLinks.map((link) =>
            link.onClick ? (
              <NavLink as="button" type="button" key={link.label} onClick={link.onClick}>
                {link.label}
              </NavLink>
            ) : (
              <Link key={link.href} href={link.href} passHref>
                <NavLink>{link.label}</NavLink>
              </Link>
            ),
          )}
        </Links>
      )}

      {onCtaClick ? (
        <Cta
          type="button"
          onClick={() => {
            closeMenu()
            onCtaClick()
          }}
        >
          {cta.label}
        </Cta>
      ) : (
        <Link href={cta.href} passHref>
          <CtaLink onClick={closeMenu}>{cta.label}</CtaLink>
        </Link>
      )}

      {panelLinks.length > 0 && (
        <MenuButton
          type="button"
          $open={menuOpen}
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </MenuButton>
      )}

      {menuOpen && (
        <Panel>
          {panelLinks.map((link) =>
            link.onClick ? (
              <PanelLink
                as="button"
                type="button"
                key={link.label}
                onClick={() => {
                  closeMenu()
                  link.onClick()
                }}
              >
                {link.label}
              </PanelLink>
            ) : (
              <Link key={link.href} href={link.href} passHref>
                {/* client-side nav keeps the page alive, so close the menu explicitly */}
                <PanelLink onClick={closeMenu}>{link.label}</PanelLink>
              </Link>
            ),
          )}
        </Panel>
      )}
    </Bar>
  )
}

export default ProgramNav
